/**
 * Authentication service to handle logout operations
 * This service acts as a bridge between API calls and user state management
 * to avoid circular dependencies
 */

type LogoutHandler = () => void;

// Private state - using closure to encapsulate the handlers array
let logoutHandlers: LogoutHandler[] = [];

/**
 * Register a logout handler
 * @param handler Function to call when logout is triggered
 */
export const registerLogoutHandler = (handler: LogoutHandler): void => {
    logoutHandlers.push(handler);
};

/**
 * Unregister a logout handler
 * @param handler Function to remove from logout handlers
 */
export const unregisterLogoutHandler = (handler: LogoutHandler): void => {
    const index = logoutHandlers.indexOf(handler);
    if (index > -1) {
        logoutHandlers.splice(index, 1);
    }
};

/**
 * Trigger logout for all registered handlers
 * This is called when API requests fail with 401/403
 */
export const triggerLogout = (): void => {
    logoutHandlers.forEach(handler => {
        try {
            handler();
        } catch (error) {
            console.error('Error in logout handler:', error);
        }
    });
};

/**
 * Clear all registered handlers (useful for testing)
 */
export const clearHandlers = (): void => {
    logoutHandlers = [];
};

// Export a service object for backward compatibility and convenience
export const authService = {
    registerLogoutHandler,
    unregisterLogoutHandler,
    triggerLogout,
    clearHandlers,
};
